/**
 * Discord 登录走弹出小窗（leo 2026-10-22）：登录页留在原处显示「等待授权」，授权在小窗里做，
 * 做完小窗把一次性 token 交回登录页、自己关掉。Discord 授权页带 `X-Frame-Options: DENY`，嵌不进 iframe，这是最接近「不离开系统」的做法。
 *
 * **为什么不用 window.opener.postMessage**：小窗中途要经过 notify-hub 和 Discord 两个外站，
 * 任何一站带了 `Cross-Origin-Opener-Policy` 就会切断 opener，回到我们域名时 opener 是 null、window.name 也被清空。
 * 所以两边只靠同源能用的东西对上：登录页开窗前在 localStorage 记一个待办（id + 时间），
 * 小窗回到 `/discord/callback?token=…` 时看到待办，就用 BroadcastChannel 把 token 发给那个 id，等对方回执。
 *
 * **token 由登录页去换票，不在小窗里换**：换完要整页重进、回到原来那一页，由登录页做最直接（票本身存 localStorage，各标签页共用）。
 * 没等到回执（原来那页关了、待办过期、浏览器没有 BroadcastChannel）小窗就自己换票，变成一个正常登录的窗口——不会因为原页面没了而登不上。
 */

const CHANNEL = "aeroplane-admin-discord-login";
const PENDING_KEY = "aeroplane_admin_discord_popup";
/** 待办只认这么久：首次在小窗里登 Discord（输密码、过验证码、两步验证）可能要几分钟 */
export const PENDING_TTL_MS = 10 * 60 * 1000;
/** 小窗等登录页回执多久。同源同进程的广播是毫秒级，给足余量也不会让人感觉卡 */
export const ACK_TIMEOUT_MS = 1500;
const POPUP_NAME = "aeroplane-discord-login";

type Msg = { type: "token"; id: string; token: string } | { type: "ack"; id: string };

export interface PopupDeps {
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
  channel: () => { postMessage(m: Msg): void; onmessage: ((e: { data: unknown }) => void) | null; close(): void } | null;
  now: () => number;
}

const defaultDeps = (): PopupDeps => ({
  storage: (() => { try { return localStorage; } catch { return null; } })(),
  channel: () => (typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL) as unknown as ReturnType<PopupDeps["channel"]>),
  now: () => Date.now(),
});

const newId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : String(Math.random()).slice(2));

export function markPending(id: string, d: PopupDeps = defaultDeps()) {
  try { d.storage?.setItem(PENDING_KEY, JSON.stringify({ id, at: d.now() })); } catch { /* 记不下就走不了小窗，handOff 会落到自己换票 */ }
}

/** 只清自己的那条：两个标签页先后点了登录，晚的那个不该被早的取消掉 */
export function clearPending(id: string, d: PopupDeps = defaultDeps()) {
  if (pendingId(d) === id) try { d.storage?.removeItem(PENDING_KEY); } catch { /* 过期自然失效 */ }
}

export function pendingId(d: PopupDeps = defaultDeps()): string | null {
  try {
    const raw = JSON.parse(d.storage?.getItem(PENDING_KEY) ?? "null") as { id?: unknown; at?: unknown } | null;
    if (!raw || typeof raw.id !== "string" || !raw.id || typeof raw.at !== "number") return null;
    const age = d.now() - raw.at;
    return age >= 0 && age <= PENDING_TTL_MS ? raw.id : null;
  } catch { return null; }
}

/**
 * 开小窗，居中在当前窗口上。被浏览器拦了返回 null，由调用方退回整页跳转。
 * 不带 noopener：带了 window.open 恒返回 null，分不清是不是被拦。
 */
export function openPopup(url: string): Window | null {
  const w = 520, h = 760;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - w) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - h) / 2));
  try { return window.open(url, POPUP_NAME, `popup=yes,width=${w},height=${h},left=${left},top=${top}`); } catch { return null; }
}

/**
 * 登录页：开始等小窗交回 token。同一个 id 只收一次（开发模式 StrictMode 会让小窗发两遍）。
 * 返回的函数停止等待，并清掉自己的待办。
 */
export function startWaiting(onToken: (token: string) => void, d: PopupDeps = defaultDeps()): { id: string; stop: () => void } {
  const id = newId();
  markPending(id, d);
  const ch = d.channel();
  let got = false;
  if (ch) ch.onmessage = (e) => {
    const m = e.data as Msg | null;
    if (!m || m.type !== "token" || m.id !== id || typeof m.token !== "string" || !m.token) return;
    ch.postMessage({ type: "ack", id });
    if (got) return;
    got = true;
    clearPending(id, d);
    onToken(m.token);
  };
  return { id, stop: () => { ch?.close(); clearPending(id, d); } };
}

/**
 * 小窗：回跳页拿到 token 后先调它。true = 已交给登录页，本窗口不要再换票；false = 没人接，自己换票。
 */
export function handOff(token: string, d: PopupDeps = defaultDeps(), timeoutMs = ACK_TIMEOUT_MS): Promise<boolean> {
  const id = pendingId(d);
  const ch = id ? d.channel() : null;
  if (!id || !ch) return Promise.resolve(false);
  return new Promise((resolve) => {
    const done = (ok: boolean) => { clearTimeout(timer); ch.close(); resolve(ok); };
    const timer = setTimeout(() => done(false), timeoutMs);
    ch.onmessage = (e) => {
      const m = e.data as Msg | null;
      if (m && m.type === "ack" && m.id === id) done(true);
    };
    ch.postMessage({ type: "token", id, token });
  });
}
